// @flow
import * as React from 'react';
import last from 'lodash/fp/last';
import reverse from 'lodash/fp/reverse';
import flatten from 'lodash/fp/flatten';

// turn string into valid xml string for parsing with root node `root`
export const stringAsXml = (
  str: string,
): string => `<?xml version="1.0" encoding="UTF-8"?>
  <root>${str}</root>`;

// turn string into a dom tree, parsing xml nodes
// TODO: implementation is platform-dependent
// TODO: throws
export const stringAsDom = (str: string): Node => {
  const doc = new DOMParser().parseFromString(
    stringAsXml(str),
    'application/xml',
  );

  const root = doc.documentElement;
  if (!root)
    throw new Error(`Failed to find documentElement of xml for ${str}`);

  const { firstChild } = root;
  if (!firstChild || firstChild.nodeName === 'parsererror') {
    throw new Error(`Failed to parse as xml ${str}`);
  }

  return root;
};

// all nodes in the given dom tree at the given depth
export const nodesAtDepth = (
  tree: Node,
  depth: number,
  current: number = 0,
): Array<Node> =>
  current === depth
    ? [tree]
    : flatten(
        Array.from(tree.childNodes || []).map((n) =>
          nodesAtDepth(n, depth, current + 1),
        ),
      );

// all nodes in the given dom tree in breadth-order
export const nodesInBreadthOrder = (
  tree: Node,
  level: number = 0,
): Array<Node> => {
  // this algo is pretty inefficent because it walks from the root of the tree
  // at each level of recursion, but i'm not worrying about it currently because
  // all our trees should be super, super shallow right now
  const nodes = nodesAtDepth(tree, level);
  return nodes.length === 0
    ? nodes // for current level
    : [...nodes, ...nodesInBreadthOrder(tree, level + 1)]; // all subsequent levels
};

// helper for replaceNodesByRenderFunctions, what happens if our node name isn't
// in the RenderFunctionMap? you get a textual representation.
export const defaultNodeBuilder = (
  children: React.ChildrenArray<any>,
  node: Node,
): React.Node => {
  if (node.nodeName === 'root') return children;

  return node.nodeType === Node.TEXT_NODE
    ? node.textContent
    : ([
        `<${node.nodeName}>`,
        ...children,
        `</${node.nodeName}>`,
      ]: $ReadOnlyArray<any>);
};

// takes children and a source node and returns react element(s)
export type RenderFunction = (React.ChildrenArray<any>, Node) => React.Node;

// map of node names to render functions
export type RenderFunctionMap = { [string]: RenderFunction };

// turns an xml-based dom tree into a react element tree by mapping dom node
// names to render functions which handle children and output dom elements.
//
// missing node names get a default implementation that just shows the tags
// inline.
// TODO: mutation
export const treeNodesMappedToRenderFunctions = (
  tree: Node,
  replacements: RenderFunctionMap,
): React.ChildrenArray<any> => {
  // this is used for looking up previously handled nodes
  const nodesToElements = new Map();

  // i tried rewriting this in terms of lodash/fp/flow but it wasn't working out
  // for some reason

  // we're running through the nodes in reverse breadth order, meaning we start
  // with leaf nodes and work back up to root, and when we're done we grab the
  // last one which will be the root node
  return last(
    reverse(nodesInBreadthOrder(tree)).map((node) => {
      // all the child nodes of this node are necessarily already in
      // nodesToElements because we're going in reverse breadth order, so we can
      // safely look up all the child elements of this node / element
      const children = flatten(
        Array.from(node.childNodes || []).map((child) => {
          const childElement = nodesToElements.get(child);
          return Array.isArray(childElement) ? childElement : [childElement];
        }),
      );

      // run our render function given the children and the node
      const element = (replacements[node.nodeName] || defaultNodeBuilder)(
        children,
        node,
      );

      // not sure why eslint isn't complaining about mutation here
      // put our node into the lookup table
      nodesToElements.set(node, element);

      return element;
    }),
  );
};

export const mightHaveTags = (str: string): boolean => !!str.match(/[<>]/);
